import { jsxs, jsx } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { useResizeObserver, useWindowEvent } from '@flex-design-system/fx-utils/hooks';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useCallbackRef } from '@radix-ui/react-use-callback-ref';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { useLayoutEffect } from '@radix-ui/react-use-layout-effect';
import { forwardRef, useMemo, useState, useRef, useCallback } from 'react';
import usePopperPresence from '../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../hooks/usePreventTransitionInteraction.mjs';
import useStyleProps from '../../hooks/useStyleProps.mjs';
import { Flex } from '../layout/Flex.mjs';
import { ScrollArea } from '../scroll-area/ScrollArea.mjs';
import InjectAside from '../utility/InjectAside.mjs';
import { SlotRoot } from '../utility/layout.mjs';
import { getAdjustPosition } from './adjust.mjs';
import { DEFAULT_COLLISION_PADDING, ROOT_MENU_SIDE_OFFSET, LIST_PADDING_Y, ASIDE_SCROLL_THRESHOLD, SUBMENU_ALIGN_OFFSET, SUBMENU_SIDE_OFFSET, DEFAULT_VIEWPORT_MAX_HEIGHT, CONTENT_MARGIN } from './constants.mjs';
import { useDropdownContext, DROPDOWN_CONTENT_NAME, DropdownProvider } from './context.mjs';
import { StyledRadioGroup, StyledLabel, LeftSlotRoot, StyledItemTitle, StyledGroup, StyledContent, StyledContentRoot, StyledContentRootScrollArea, StyledViewport, WithoutPortal, StyledAsideRoot, StyledAsideAffixRoot, StyledSubTrigger, StyledSubContent, StyledCheckboxItem, StyledRadioItem, StyledItem, StyledItemDescription, StyledAffixRoot, StyledContentFooterRoot, StyledSeparatorRoot, StyledSeparator } from './styled.mjs';

const DropdownRadioGroup = forwardRef(({ title, children, ...props }, ref) => {
  return /* @__PURE__ */ jsxs(StyledRadioGroup, { ref, ...props, children: [
    !isNil(title) ? /* @__PURE__ */ jsx(StyledLabel, { children: title }) : null,
    children
  ] });
});
const DropdownMenuItemTitle = forwardRef(({ children, leftSlot, rightSlot, ...props }, ref) => {
  const child = useMemo(() => {
    if (isNil(leftSlot)) {
      return children;
    }
    return /* @__PURE__ */ jsxs(
      Flex,
      {
        align: "center",
        css: {
          gap: 8
        },
        children: [
          !isNil(leftSlot) && /* @__PURE__ */ jsx(LeftSlotRoot, { children: leftSlot }),
          /* @__PURE__ */ jsx("div", { children })
        ]
      }
    );
  }, [children, leftSlot]);
  return /* @__PURE__ */ jsxs(StyledItemTitle, { ...props, ref, children: [
    child,
    !isNil(rightSlot) && /* @__PURE__ */ jsx(SlotRoot, { css: { marginLeft: "auto" }, children: rightSlot })
  ] });
});
function DropdownSeparator(props) {
  return /* @__PURE__ */ jsx(StyledSeparatorRoot, { role: "separator", ...props, children: /* @__PURE__ */ jsx(StyledSeparator, {}) });
}
const DropdownGroup = forwardRef(({ title, children, ...props }, ref) => {
  return /* @__PURE__ */ jsxs(StyledGroup, { ref, ...props, children: [
    !isNil(title) ? /* @__PURE__ */ jsx(StyledLabel, { children: title }) : null,
    children
  ] });
});
const DropdownMenuTrigger = forwardRef(({ children, asChild = true, ...props }, forwardedRef) => {
  const { onTriggerChange } = useDropdownContext("Dropdown.Trigger");
  const composedRef = useComposedRefs(forwardedRef, onTriggerChange);
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Trigger,
    {
      ref: composedRef,
      asChild,
      ...props,
      children
    }
  );
});
const DropdownMenu = (props) => {
  const {
    children,
    open: openProp,
    defaultOpen,
    onOpenChange,
    onClose,
    ...dropdownMenuProps
  } = props;
  const [submenuTrigger, setSubmenuTrigger] = useState(null);
  const [trigger, setTrigger] = useState(null);
  const [contentMaxHeight, setContentMaxHeight] = useState(
    DEFAULT_VIEWPORT_MAX_HEIGHT
  );
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const handleClose = useCallbackRef(onClose);
  return /* @__PURE__ */ jsx(
    DropdownProvider,
    {
      open,
      submenuTrigger,
      onSubmenuTriggerChange: setSubmenuTrigger,
      trigger,
      onTriggerChange: setTrigger,
      contentMaxHeight,
      onContentMaxHeightChange: setContentMaxHeight,
      onClose: handleClose,
      children: /* @__PURE__ */ jsx(
        DropdownMenuPrimitive.Root,
        {
          open,
          onOpenChange: setOpen,
          ...dropdownMenuProps,
          children
        }
      )
    }
  );
};
const DropdownMenuContent = forwardRef((props, ref) => {
  const {
    align = "start",
    children,
    sideOffset,
    alignOffset,
    affix,
    footer,
    leftAside,
    rightAside,
    withoutPortal,
    zIndex,
    collisionPadding = DEFAULT_COLLISION_PADDING,
    ...contentProps
  } = props;
  const { styleProps, ...restContentProps } = useStyleProps(contentProps);
  const { open, trigger, onClose, onContentMaxHeightChange } = useDropdownContext(DROPDOWN_CONTENT_NAME);
  const [contentElement, setContentElement] = useState(null);
  const viewportRef = useRef(null);
  const [affixElement, setAffixElement] = useState(null);
  const [footerElement, setFooterElement] = useState(
    null
  );
  const { ref: presenceRef } = usePopperPresence({
    open,
    onPresenceChange: (isPresent) => !isPresent && onClose?.()
  });
  const preventTransitionInteraction = usePreventTransitionInteraction({
    open
  });
  const composedRefs = useComposedRefs(ref, presenceRef, (node) => {
    setContentElement(node);
  });
  const rootMenuPosition = useCallback(() => {
    if (contentElement !== null && viewportRef.current !== null && trigger !== null) {
      const [, { maxHeight }] = getAdjustPosition({
        trigger,
        availableHeight: window.innerHeight,
        contentElement,
        alignOffset,
        safeAreaOffset: CONTENT_MARGIN});
      const affixHeight = affixElement?.scrollHeight ?? 0;
      const footerHeight = footerElement?.scrollHeight ?? 0;
      const comparedMaxHeight = Math.min(
        maxHeight,
        DEFAULT_VIEWPORT_MAX_HEIGHT
      );
      const updatedMaxHeight = comparedMaxHeight - affixHeight - footerHeight;
      onContentMaxHeightChange(updatedMaxHeight);
      viewportRef.current.style.maxHeight = `${updatedMaxHeight}px`;
    }
  }, [
    contentElement,
    trigger,
    alignOffset,
    affixElement?.scrollHeight,
    footerElement?.scrollHeight,
    onContentMaxHeightChange
  ]);
  useLayoutEffect(() => {
    rootMenuPosition();
  }, [rootMenuPosition]);
  useResizeObserver(trigger, rootMenuPosition);
  useWindowEvent("resize", rootMenuPosition);
  const affixStyle = useCallback(() => {
    if (affixElement !== null && viewportRef.current !== null) {
      const viewportStyle = getComputedStyle(viewportRef.current);
      const { maxWidth, minWidth, width } = viewportStyle;
      [
        { name: "maxWidth", value: maxWidth },
        { name: "minWidth", value: minWidth },
        { name: "width", value: width }
      ].forEach(({ name, value }) => {
        affixElement.style[name] = value;
      });
    }
  }, [affixElement]);
  useLayoutEffect(() => {
    affixStyle();
  }, [affixStyle]);
  useResizeObserver(viewportRef.current, affixStyle);
  const zIndexStyle = usePopperZIndexStyle(open, zIndex);
  const content = /* @__PURE__ */ jsxs(
    StyledContent,
    {
      ...restContentProps,
      onKeyDownCapture: (e) => {
        restContentProps.onKeyDownCapture?.(e);
        preventTransitionInteraction.onKeyDownCapture(e);
      },
      style: preventTransitionInteraction.style,
      align,
      sideOffset: sideOffset ?? ROOT_MENU_SIDE_OFFSET,
      alignOffset,
      css: zIndexStyle,
      collisionPadding,
      ref: composedRefs,
      children: [
        leftAside ? /* @__PURE__ */ jsx(InjectAside, { side: "left", children: leftAside }) : null,
        /* @__PURE__ */ jsxs(StyledContentRoot, { "data-scope": "dropdown", "data-part": "content-root", children: [
          !isNil(affix) ? /* @__PURE__ */ jsx(
            "div",
            {
              "data-scope": "dropdown",
              "data-part": "content-affix",
              ref: setAffixElement,
              children: affix
            }
          ) : null,
          /* @__PURE__ */ jsx(
            StyledContentRootScrollArea,
            {
              "data-scope": "dropdown",
              "data-part": "content-scroll-area",
              children: /* @__PURE__ */ jsx(StyledViewport, { ...styleProps, ref: viewportRef, children })
            }
          ),
          !isNil(footer) ? /* @__PURE__ */ jsx(
            "div",
            {
              "data-scope": "dropdown",
              "data-part": "content-footer",
              ref: setFooterElement,
              children: footer
            }
          ) : null
        ] }),
        rightAside ? /* @__PURE__ */ jsx(InjectAside, { side: "right", children: rightAside }) : null
      ]
    }
  );
  if (withoutPortal) {
    return /* @__PURE__ */ jsx(WithoutPortal, { css: zIndexStyle, children: content });
  }
  return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { style: zIndexStyle, children: content });
});
const DropdownMenuAsideRoot = forwardRef((props, ref) => {
  const { children, affix, side, ...asideRootProps } = props;
  const [dividerVisible, setDividerVisible] = useState(false);
  const { contentMaxHeight } = useDropdownContext("DropdownContent.Aside");
  const [affixElement, setAffixElement] = useState(null);
  const [viewportElement, setViewportElement] = useState(
    null
  );
  const composedRef = useComposedRefs(ref, (node) => setViewportElement(node));
  const asideViewportMaxHeight = useMemo(() => {
    return contentMaxHeight + LIST_PADDING_Y * 2;
  }, [contentMaxHeight]);
  const handleAsideViewportScroll = useCallback(
    (e) => {
      const { scrollTop } = e.currentTarget;
      setDividerVisible(scrollTop > ASIDE_SCROLL_THRESHOLD);
    },
    []
  );
  const viewportStyle = useCallback(() => {
    if (affixElement !== null && viewportElement !== null) {
      const affixHeight = affixElement.scrollHeight ?? 0;
      const maxHeight = contentMaxHeight - affixHeight;
      viewportElement.style.maxHeight = `${maxHeight}px`;
    }
  }, [affixElement, contentMaxHeight, viewportElement]);
  useLayoutEffect(() => {
    viewportStyle();
  }, [viewportStyle]);
  useWindowEvent("resize", viewportStyle);
  return /* @__PURE__ */ jsxs(StyledAsideRoot, { side, children: [
    /* @__PURE__ */ jsx(
      StyledAsideAffixRoot,
      {
        ref: setAffixElement,
        css: {
          boxShadow: dividerVisible ? "inset 0px -1px 0px rgba(0, 0, 0, 0.06)" : "none"
        },
        children: affix
      }
    ),
    /* @__PURE__ */ jsx(ScrollArea, { style: { flexGrow: 1, maxHeight: asideViewportMaxHeight }, children: /* @__PURE__ */ jsx(
      ScrollArea.Viewport_Legacy,
      {
        ref: composedRef,
        ...asideRootProps,
        onScroll: composeEventHandlers(
          asideRootProps.onScroll,
          handleAsideViewportScroll
        ),
        children
      }
    ) })
  ] });
});
const SubMenuTrigger = forwardRef(
  (props, ref) => {
    const context = useDropdownContext("Dropdown.SubMenuTriggerItem");
    const composedRef = useComposedRefs(
      ref,
      (node) => context.onSubmenuTriggerChange(node)
    );
    return /* @__PURE__ */ jsx(StyledSubTrigger, { ...props, ref: composedRef });
  }
);
const SubContent = forwardRef(
  (props, ref) => {
    const {
      container,
      children,
      sideOffset = SUBMENU_SIDE_OFFSET,
      alignOffset = SUBMENU_ALIGN_OFFSET,
      ...contentProps
    } = props;
    const { styleProps, ...restContentProps } = useStyleProps(contentProps);
    return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { container, children: /* @__PURE__ */ jsx(
      StyledSubContent,
      {
        ...restContentProps,
        sideOffset,
        alignOffset,
        ref,
        children: /* @__PURE__ */ jsx(ScrollArea, { children: /* @__PURE__ */ jsx(StyledViewport, { ...styleProps, children }) })
      }
    ) });
  }
);
const Legacy_Dropdown = DropdownMenu;
Legacy_Dropdown.Sub = DropdownMenuPrimitive.Sub;
Legacy_Dropdown.SubTrigger = SubMenuTrigger;
Legacy_Dropdown.SubContent = SubContent;
Legacy_Dropdown.Content = DropdownMenuContent;
Legacy_Dropdown.CheckboxItem = StyledCheckboxItem;
Legacy_Dropdown.RadioItem = StyledRadioItem;
Legacy_Dropdown.RadioGroup = DropdownRadioGroup;
Legacy_Dropdown.Item = StyledItem;
Legacy_Dropdown.ItemIndicator = DropdownMenuPrimitive.ItemIndicator;
Legacy_Dropdown.ItemTitle = DropdownMenuItemTitle;
Legacy_Dropdown.ItemDescription = StyledItemDescription;
Legacy_Dropdown.Group = DropdownGroup;
Legacy_Dropdown.Trigger = DropdownMenuTrigger;
Legacy_Dropdown.Label = StyledLabel;
Legacy_Dropdown.Divider = DropdownSeparator;
Legacy_Dropdown.Aside = DropdownMenuAsideRoot;
Legacy_Dropdown.AffixRoot = StyledAffixRoot;
Legacy_Dropdown.ContentFooter = StyledContentFooterRoot;

export { Legacy_Dropdown };
//# sourceMappingURL=Dropdown.mjs.map
